import { AuthStep } from 'constants/authStep';

import { SyntheticEvent, useState } from 'react';

import { Button, Checkbox, FormControlLabel, Box } from '@mui/material';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useAuthProvider } from 'react-admin';
import { FieldValues, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert } from 'shared/mui/Alert/Alert';
import { PasswordVisibilityIconButton } from 'shared/mui/IconButtons/PasswordVisibilityIconButton/PasswordVisibilityIconButton';
import { TextField } from 'shared/mui/TextField/TextField';
import { executeCaptchaWithData } from 'utils';

type Props = {
  updateStep: (step: AuthStep) => void;
  error?: string | null;
  setError: (error: string | null) => void;
};

export const LoginCredentialsForm: React.FC<Props> = ({ updateStep, error, setError }) => {
  const translate = useTypedTranslate();

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const { register, handleSubmit, watch } = useForm();
  const [reddyNumber, password] = watch(['reddyNumber', 'password']);

  const authProvider = useAuthProvider();
  const navigate = useNavigate();
  const { state } = useLocation();

  const hasError = Boolean(error);

  const submit = (captchaToken: string, { reddyNumber, password, remember }: FieldValues) => {
    authProvider
      ?.preLogin({ captchaToken, reddyNumber, password, remember })
      .then(() => {
        updateStep(authProvider?.step);
        return navigate('/login', { state });
      })
      .catch(() => setError(translate('app.login.invalidNumberOrPassword')));
  };

  const proxySubmit = (data: FieldValues) => {
    setError(null);
    executeCaptchaWithData(submit, data);
  };

  const navigateToRestorePassword = (e: SyntheticEvent) => {
    e.preventDefault();
    navigate('/restore_password');
  };

  return (
    <Box>
      {hasError && (
        <Alert severity="error" sx={{ marginBottom: '24px' }}>
          {error}
        </Alert>
      )}
      <form noValidate onSubmit={handleSubmit(proxySubmit)}>
        <TextField
          {...register('reddyNumber')}
          error={hasError}
          fullWidth
          id="reddyNumber"
          label={translate('app.login.numberReddy')}
          margin="normal"
          name="reddyNumber"
          size="medium"
          sx={{ marginBottom: '24px !important' }}
          variant="outlined"
        />
        <TextField
          {...register('password')}
          InputProps={{
            endAdornment: (
              <PasswordVisibilityIconButton
                isVisible={isPasswordVisible}
                onClick={() => setIsPasswordVisible((value) => !value)}
              />
            ),
          }}
          autoComplete="current-password"
          error={hasError}
          fullWidth
          id="password"
          label={translate('app.login.password')}
          margin="normal"
          name="password"
          size="medium"
          sx={{ marginBottom: '24px !important', marginTop: 0 }}
          type={isPasswordVisible ? 'text' : 'password'}
          variant="outlined"
        />
        <FormControlLabel
          control={<Checkbox {...register('remember')} color="primary" name="remember" />}
          label={translate('app.login.rememberMe')}
          sx={{ marginTop: 0, marginBottom: '24px' }}
        />
        <Box sx={{ display: 'flex', gap: '16px' }}>
          <Button
            color="primary"
            disabled={!reddyNumber || !password}
            fullWidth
            type="submit"
            variant="contained"
          >
            {translate('app.login.signIn')}
          </Button>
          <Button fullWidth onClick={navigateToRestorePassword} size="small" variant="outlined">
            {translate('app.login.forgotPassword')}
          </Button>
        </Box>
      </form>
    </Box>
  );
};
